import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CalendarMonth as CalendarIcon,
  AttachMoney as MoneyIcon,
  Group as GroupIcon,
  Description as FileIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface PADFileBatch {
  id: number;
  batch_date: string;
  file_name: string;
  file_path: string | null;
  total_transactions: number;
  total_amount: number;
  status: 'generating' | 'generated' | 'submitted' | 'processed';
  generated_by: number | null;
  generated_by_name?: string;
  submitted_at: string | null;
  notes: string | null;
  created_at: string;
}

interface PADTransaction {
  id: number;
  custid: number;
  company_name: string;
  institution_name: string;
  amount: number;
  scheduled_withdrawal_date: string;
  status: string;
}

const PADFileManagement: React.FC = () => {
  const theme = useTheme();
  const { member } = useAuth();
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState<PADFileBatch[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<PADFileBatch | null>(null);
  const [batchTransactions, setBatchTransactions] = useState<PADTransaction[]>([]);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (member?.memberType === 'master_admin') {
      initializeYears();
      fetchBatches();
    }
  }, [member, selectedYear]);

  const initializeYears = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth(); // 0-11
    const years = [];
    
    // Add past years (assuming PAD files started in 2020)
    for (let year = 2020; year <= currentYear; year++) {
      years.push(year);
    }
    
    // Add next year if we're in December
    if (currentMonth === 11) { // December
      years.push(currentYear + 1);
    }
    
    setAvailableYears(years);
  };

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/endpoints/pad-files?year=${selectedYear}`);
      if (response.data.success) {
        setBatches(response.data.data.batches);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load PAD files');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (batch: PADFileBatch) => {
    try {
      const response = await api.get(`/endpoints/pad-files/${batch.id}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', batch.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Failed to download file');
    }
  };

  const handleViewDetails = async (batch: PADFileBatch) => {
    try {
      setSelectedBatch(batch);
      const response = await api.get(`/endpoints/pad-files/${batch.id}/transactions`);
      if (response.data.success) {
        setBatchTransactions(response.data.data.transactions);
      }
      setDetailsOpen(true);
    } catch (err: any) {
      setError('Failed to load transaction details');
    }
  };

  const handleMarkSubmitted = async (batchId: number) => {
    try {
      await api.put(`/endpoints/pad-files/${batchId}/status`, {
        status: 'submitted'
      });
      fetchBatches();
    } catch (err: any) {
      setError('Failed to update status');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'generated':
        return 'success';
      case 'submitted':
        return 'info';
      case 'processed':
        return 'default';
      default:
        return 'warning';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'generated':
        return <CheckCircleIcon fontSize="small" />;
      case 'submitted':
        return <InfoIcon fontSize="small" />;
      case 'processed':
        return <CheckCircleIcon fontSize="small" />;
      default:
        return <WarningIcon fontSize="small" />;
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (member?.memberType !== 'master_admin') {
    return (
      <Alert severity="error">
        You do not have permission to access this section.
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">PAD File Management</Typography>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            label="Year"
            size="small"
          >
            {availableYears.map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Files
                  </Typography>
                  <Typography variant="h4">
                    {batches.length}
                  </Typography>
                </Box>
                <FileIcon color="primary" sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Amount
                  </Typography>
                  <Typography variant="h4">
                    {formatCurrency(batches.reduce((sum, b) => sum + b.total_amount, 0))}
                  </Typography>
                </Box>
                <MoneyIcon color="success" sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Transactions
                  </Typography>
                  <Typography variant="h4">
                    {batches.reduce((sum, b) => sum + b.total_transactions, 0)}
                  </Typography>
                </Box>
                <GroupIcon color="info" sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Pending
                  </Typography>
                  <Typography variant="h4">
                    {batches.filter(b => b.status === 'generated').length}
                  </Typography>
                </Box>
                <WarningIcon color="warning" sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Batch Date</TableCell>
                <TableCell>File Name</TableCell>
                <TableCell align="center">Transactions</TableCell>
                <TableCell align="right">Total Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Generated By</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No PAD files found for {selectedYear}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                batches.map((batch) => (
                  <TableRow key={batch.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarIcon fontSize="small" color="action" />
                        {formatDate(batch.batch_date)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {batch.file_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {batch.total_transactions}
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="medium">
                        {formatCurrency(batch.total_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(batch.status)}
                        label={batch.status}
                        color={getStatusColor(batch.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {batch.generated_by_name || 'System'}
                    </TableCell>
                    <TableCell>
                      {batch.submitted_at ? formatDate(batch.submitted_at) : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(batch)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {(batch.file_path || batch.status === 'generated') && (
                        <Tooltip title="Download File">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(batch)}
                            color="primary"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {batch.status === 'generated' && (
                        <Tooltip title="Mark as Submitted">
                          <IconButton
                            size="small"
                            onClick={() => handleMarkSubmitted(batch.id)}
                            color="success"
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          PAD File Details - {selectedBatch?.file_name}
        </DialogTitle>
        <DialogContent dividers>
          {selectedBatch && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Batch Date
                  </Typography>
                  <Typography>
                    {formatDate(selectedBatch.batch_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Total Amount
                  </Typography>
                  <Typography>
                    {formatCurrency(selectedBatch.total_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box>
                    <Chip
                      icon={getStatusIcon(selectedBatch.status)}
                      label={selectedBatch.status}
                      color={getStatusColor(selectedBatch.status)}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Total Transactions
                  </Typography>
                  <Typography>
                    {selectedBatch.total_transactions}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Transactions
              </Typography>
              
              <List>
                {batchTransactions.map((transaction, index) => (
                  <React.Fragment key={transaction.id}>
                    <ListItem>
                      <ListItemText
                        primary={transaction.company_name}
                        secondary={
                          <Box>
                            <Typography variant="caption" component="span">
                              {transaction.institution_name} • 
                              Withdrawal: {formatDate(transaction.scheduled_withdrawal_date)}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Typography variant="h6">
                          {formatCurrency(transaction.amount)}
                        </Typography>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < batchTransactions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          {(selectedBatch?.file_path || selectedBatch?.status === 'generated') && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => selectedBatch && handleDownload(selectedBatch)}
            >
              Download File
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PADFileManagement;